import React, { FC } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import { Order, OrderItem } from '../../shared/orders/model';
import { refundRequestReasons } from '../models';
import { useTranslation } from '../translations';
import { displayName, generateRange, sumBy } from '../utils';
import { Currency } from './Currency';
import { JSONForm } from './json-form/JSONForm';

interface RefundRequestModalProps {
  readonly order: Order;
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
}

type RefundRequestFormData = Record<string, number>;

interface RefundRequestPostData {
  refundItems: Record<number, number>;
  refundReason: string;
  refundAddInfo?: string;
}

export const RefundRequestModal: FC<RefundRequestModalProps> = ({ order, onCancel, onSuccess }) => {
  const { ts } = useTranslation();
  function onRefundRequest(): void {
    toast.success(ts('refund_request_modal.the_refund_request_has_been'));
    onSuccess();
  }

  function calculateRemainingQuantity(item: OrderItem): number {
    return (
      item.quantity -
      sumBy(order.refunds, ({ items, status }) => {
        const refundItem = items.find(({ itemId }) => itemId === item.id);
        return !refundItem || status === 'denied' ? 0 : refundItem.quantity;
      })
    );
  }

  const totalRemainingQuantity = sumBy(order.orderItems, (item) =>
    calculateRemainingQuantity(item),
  );

  if (totalRemainingQuantity === 0) {
    return (
      <Modal className="modal-large" id="refundModal" isOpen>
        <ModalHeader>
          {ts('refund_request_modal.request_refund_order_orderid', order.id)}
        </ModalHeader>
        <ModalBody>
          <p>{ts('refund_request_modal.there_are_no_further_items')}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel}>
            {ts('common.cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal className="modal-large" id="refundModal" isOpen key={1}>
      <JSONForm<object, RefundRequestFormData & RefundRequestPostData>
        method="post"
        onSuccess={onRefundRequest}
        path={`/api/orders/${order.id}/refund/request`}
      >
        <ModalHeader>
          {ts('refund_request_modal.request_refund_order_orderid', order.id)}
        </ModalHeader>
        <ModalBody>
          <p>{ts('refund_request_modal.the_refund_request_will_be')}</p>
          <Table striped>
            <thead>
              <tr>
                <th>{ts('common.product_name')}</th>
                <th>{ts('common.unit_price')}</th>
                <th>{ts('common.quantity')}</th>
              </tr>
            </thead>
            <tbody>
              {order.orderItems.map((item) => {
                const key = `orderItem${item.id}`;
                const remainingQuantity = calculateRemainingQuantity(item);

                if (remainingQuantity === 0) {
                  return null;
                }

                // Free addons does not deserve a refund
                if (item.parentOrderItemId) {
                  return null;
                }

                return (
                  <tr key={item.id}>
                    <td>{displayName(item.product)}</td>
                    <td>
                      <Currency value={item.price} />
                    </td>
                    <td>
                      <Input
                        disabled={remainingQuantity === 0}
                        id={`${key}Quantity`}
                        name={`refundItems[${item.id}]`}
                        type="select"
                      >
                        {generateRange(0, remainingQuantity).map((idx) => (
                          <option key={idx}>{idx}</option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <br />
          <FormGroup row>
            <Label for="refundReason" lg={5} sm={12}>
              {ts('refund_request_modal.reason_to_refund')}
            </Label>
            <Col lg={7} sm={12}>
              <Input id="refundReason" name="refundReason" type="select">
                {refundRequestReasons.map(({ key, description }) => (
                  <option key={key} value={key}>
                    {description}
                  </option>
                ))}
              </Input>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="refundAddInfo" lg={5} sm={12}>
              {ts('common.additional_info')}
            </Label>
            <Col lg={7} sm={12}>
              <Input id="refundAddInfo" name="refundAddInfo" type="textarea" />
            </Col>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel}>
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="primary" id="requestRefund" type="submit">
            {ts('common.request_refund')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};
